import { FormikProps } from 'formik';

import React from 'react';
import styled from 'styled-components/macro';
import { assertNever } from '../../../helpers/neverHelpers';
import { formatCurrency } from '../../../helpers/stringHelpers';
import { FormFieldRow } from '../../../shared/form/FormFieldRow';
import { SelectField } from '../../../shared/form/inputs/SelectField';
import { TextInputField } from '../../../shared/form/inputs/TextInputField';
import { ReadOnlyField } from '../../../shared/form/ReadOnlyField';
import { useBillOfMaterialsPricingModelInfo } from '../../bill-of-materials/BillOfMaterialsContext';
import { BillOfMaterialsItemFormModel } from '../../bill-of-materials/billOfMaterialsItemFormModel';
import {
  BillOfMaterialsPartsById,
  calculateTotalCost,
  calculateTotalPriceBeforeDiscount,
} from '../../bill-of-materials/dataForEditBillOfMaterialsForm';
import { EditDesignBillOfMaterialsFormModel } from '../../designs/edit-bill-of-materials/editDesignBillOfMaterialsFormModel';
import {
  billOfMaterialsMaxDiscountPercentage,
  billOfMaterialsMinDiscountPercentage,
  ViewOrEditQuoteBillOfMaterialsFormModel,
  manualDiscountSelectOptions,
  ManualDiscountTypeCode,
} from './viewOrEditQuoteBillOfMaterialsFormModel';

export const calculateStartingPrice = (
  isUsingLegacyPricingModel: boolean,
  totalPriceBeforeDiscount: number,
  discountPercentage?: number | null,
): number | null => {
  if (!isUsingLegacyPricingModel) {
    return totalPriceBeforeDiscount;
  }

  const discountMultiplier =
    discountPercentage == null ||
    discountPercentage < billOfMaterialsMinDiscountPercentage ||
    discountPercentage > billOfMaterialsMaxDiscountPercentage
      ? null
      : (100 - discountPercentage) / 100;

  return discountMultiplier == null ? null : totalPriceBeforeDiscount * discountMultiplier;
};

const calculateTotalPriceAfterDiscount = (
  manualDiscountAmount: number | null,
  manualDiscountTypeCode: ManualDiscountTypeCode,
  startingPrice: number | null,
  totalCost: number,
) => {
  switch (manualDiscountTypeCode) {
    case 'AdditionalDiscountPercentage': {
      const additionalDiscountMultiplier = manualDiscountAmount == null ? null : (100 - manualDiscountAmount) / 100;
      return startingPrice == null || additionalDiscountMultiplier == null
        ? null
        : startingPrice * additionalDiscountMultiplier;
    }
    case 'AdditionalDiscountInPounds': {
      return manualDiscountAmount == null || startingPrice == null ? null : startingPrice - manualDiscountAmount;
    }
    case 'TargetMargin': {
      return manualDiscountAmount == null ? null : totalCost / (1 - manualDiscountAmount / 100);
    }
    default: {
      assertNever(manualDiscountTypeCode);
    }
  }
};

type Props<TFormModel extends EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel> = {
  manualDiscountAmountFieldName: string;
  manualDiscountTypeFieldName: string;
  manualDiscountAmount: number | null;
  manualDiscountTypeCode: ManualDiscountTypeCode;
  items: Array<BillOfMaterialsItemFormModel>;
  partsById: BillOfMaterialsPartsById;
  setFieldValue: FormikProps<TFormModel>['setFieldValue'];
  discountPercentageFieldName: string;
  discountPercentage?: number | null;
};

export const BillOfMaterialsDiscountFields = <
  TFormModel extends EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel
>({
  manualDiscountAmountFieldName,
  manualDiscountTypeFieldName,
  manualDiscountAmount,
  manualDiscountTypeCode,
  discountPercentageFieldName,
  discountPercentage,
  items,
  partsById,
  setFieldValue,
}: Props<TFormModel>) => {
  const { isUsingLegacyPricingModel, selectedCustomerTypeOption } = useBillOfMaterialsPricingModelInfo();

  const totalPriceBeforeDiscount = calculateTotalPriceBeforeDiscount(
    isUsingLegacyPricingModel,
    items,
    partsById,
    selectedCustomerTypeOption,
  );

  const totalCost = calculateTotalCost(items, partsById);

  const startingPrice = calculateStartingPrice(isUsingLegacyPricingModel, totalPriceBeforeDiscount, discountPercentage);

  const totalAfterDiscount = calculateTotalPriceAfterDiscount(
    manualDiscountAmount,
    manualDiscountTypeCode,
    startingPrice,
    totalCost,
  );

  const margin = totalAfterDiscount == null ? null : 100 * (1 - totalCost / totalAfterDiscount);

  return (
    <>
      <FormFieldRow>
        {isUsingLegacyPricingModel && <TextInputField name={discountPercentageFieldName} label="Discount (%)" />}
        <ReadOnlyCurrencyField
          name=""
          label="Total before Discount"
          value={formatCurrency(totalPriceBeforeDiscount, { showPoundSymbol: true })}
        />
        {isUsingLegacyPricingModel && (
          <ReadOnlyCurrencyField
            name=""
            label="Starting Price"
            value={startingPrice == null ? '' : formatCurrency(startingPrice, { showPoundSymbol: true })}
          />
        )}
      </FormFieldRow>
      <FormFieldRow>
        <SelectField
          name={manualDiscountTypeFieldName}
          label="Manual Discount Type"
          options={manualDiscountSelectOptions}
          onChange={() => setFieldValue(manualDiscountAmountFieldName, '0')}
        />
        <TextInputField name={manualDiscountAmountFieldName} label="Manual Discount Amount" />
      </FormFieldRow>
      <FormFieldRow>
        <ReadOnlyCurrencyField name="" label="Margin" value={margin == null ? '' : `${margin.toFixed(2)} %`} />
        <ReadOnlyCurrencyField
          name=""
          label="Total after Discount"
          value={totalAfterDiscount == null ? '' : formatCurrency(totalAfterDiscount, { showPoundSymbol: true })}
        />
        <ReadOnlyCurrencyField
          name=""
          label="Total Cost of Materials"
          value={formatCurrency(totalCost, { showPoundSymbol: true })}
        />
      </FormFieldRow>
    </>
  );
};

const ReadOnlyCurrencyField = styled(ReadOnlyField)`
  letter-spacing: 1px;
`;
